"use client"

import { useState, useEffect, useRef, type Dispatch, type SetStateAction } from "react"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import Image from "next/image"
import { Phone, Video, Info, Send, Heart, MessageCircle, Bookmark, Share2, X, Minus } from "lucide-react"
import * as Dialog from "@radix-ui/react-dialog"
import { createPortal } from "react-dom"
import { InstagramChat } from "./instagram-chat"
import { LinkedInChat } from "./linkedin-chat"
import { PlatformTabs } from "./platform-tabs"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"

interface Message {
  id: string
  text: string
  author: "me" | "contact"
  source: "website"
  sending: boolean
  sent_at: string
  attachments: any[]
  isTyping?: boolean
}

export function FloatingChatHero( {activeTab, setActiveTab, instagramMessages, setInstagramMessages, linkedinMessages, setLinkedinMessages, instagramInput, setInstagramInput, linkedinInput, setLinkedinInput, handleInstagramSend, handleLinkedinSend, visible, open, onOpenChange, unreadCountInstagram, unreadCountLinkedin}: {activeTab: "instagram" | "linkedin", setActiveTab: (p: "instagram" | "linkedin") => void, instagramMessages: Message[], setInstagramMessages: Dispatch<SetStateAction<Message[]>>, linkedinMessages: Message[], setLinkedinMessages: Dispatch<SetStateAction<Message[]>>, instagramInput: string, setInstagramInput: (p: string) => void, linkedinInput: string, setLinkedinInput: (p: string) => void, handleInstagramSend: () => void, handleLinkedinSend: () => void, visible: boolean, open?: boolean, onOpenChange?: (open: boolean) => void, unreadCountInstagram?: number, unreadCountLinkedin?: number}) {

  const [internalOpen, setInternalOpen] = useState(false)
  const isChatOpen = open ?? internalOpen
  const setIsChatOpen = (next: boolean) => {
    onOpenChange?.(next)
    if (open === undefined) setInternalOpen(next)
  }
  const [isMobile, setIsMobile] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [recaptchaOpen, setRecaptchaOpen] = useState(false)
  const [shouldOpenAfterCaptcha, setShouldOpenAfterCaptcha] = useState(false)

 


  // Track viewport width to switch between dialog (mobile) and portal (desktop)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mql = window.matchMedia('(max-width: 767px)')
    const setMatches = () => setIsMobile(mql.matches)
    setMatches()
    mql.addEventListener?.('change', setMatches)
    return () => mql.removeEventListener?.('change', setMatches)
  }, [])

  // Lock body scroll on mobile when chat is open
  useEffect(() => {
    if (!isChatOpen || !isMobile) return
    const scrollY = window.scrollY
    const prev = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      scrollBehavior: document.documentElement.style.scrollBehavior,
    }
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.width = '100%'
    return () => {
      const top = document.body.style.top
      document.body.style.position = prev.position
      document.body.style.top = prev.top
      document.body.style.left = prev.left
      document.body.style.right = prev.right
      document.body.style.width = prev.width
      // Force instant scroll jump without smooth behavior
      document.documentElement.style.scrollBehavior = 'auto'
      const y = Math.abs(parseInt(top || '0', 10)) || 0
      window.scrollTo(0, y)
      // Restore prior scroll-behavior
      document.documentElement.style.scrollBehavior = prev.scrollBehavior
    }
  }, [isChatOpen, isMobile])

  // Ensure portal renders only after mount to avoid hydration mismatch
  useEffect(() => {
    setMounted(true)
  }, [])

  // Listen for body attribute set by chat-hero when reCAPTCHA opens
  useEffect(() => {
    if (typeof document === 'undefined') return
    const update = () => setRecaptchaOpen(document.body.getAttribute('data-recaptcha-open') === '1')
    update()
    const mo = new MutationObserver(update)
    mo.observe(document.body, { attributes: true, attributeFilter: ['data-recaptcha-open'] })
    return () => mo.disconnect()
  }, [])

  // When captcha solved, open chat automatically if user initiated from the button
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onSolved = () => {
      if (shouldOpenAfterCaptcha) {
        // Give iOS a breath to commit DOM for captcha success before opening dialog
        setTimeout(() => {
          setIsChatOpen(true)
          setShouldOpenAfterCaptcha(false)
        }, 50)
      }
    }
    window.addEventListener('theos:captcha-solved', onSolved)
    return () => window.removeEventListener('theos:captcha-solved', onSolved)
  }, [shouldOpenAfterCaptcha])

  // Close chat if it becomes hidden
  useEffect(() => {
    if (!visible) {
      setIsChatOpen(false)
    }
  }, [visible])

  // Respect prefers-reduced-motion
  const prefersReducedMotion = useReducedMotion()
  const easeOutCubic: [number, number, number, number] = [0.16, 1, 0.3, 1]
  const transitionFast = { duration: prefersReducedMotion ? 0 : 0.2, ease: easeOutCubic }
  const transitionSlide = { duration: prefersReducedMotion ? 0 : 0.25, ease: easeOutCubic }

  // Adapt height to on-screen keyboard using visualViewport when available
  useEffect(() => {
    if (!isMobile || !isChatOpen || typeof window === 'undefined') return
    const vv: VisualViewport | undefined = (window as any).visualViewport
    const setVvh = () => {
      const height = vv?.height ?? window.innerHeight
      document.documentElement.style.setProperty('--vvh', `${height}px`)
    }
    setVvh()
    vv?.addEventListener('resize', setVvh)
    vv?.addEventListener('scroll', setVvh)
    window.addEventListener('resize', setVvh)
    return () => {
      vv?.removeEventListener('resize', setVvh)
      vv?.removeEventListener('scroll', setVvh)
      window.removeEventListener('resize', setVvh)
      document.documentElement.style.removeProperty('--vvh')
    }
  }, [isMobile, isChatOpen])

  const unreadCount = activeTab === "instagram" ? (unreadCountInstagram ?? 0) : (unreadCountLinkedin ?? 0)

  return (
    <Dialog.Root open={isChatOpen} onOpenChange={setIsChatOpen}>
      {mounted && typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {visible && (
            <motion.button
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 10 }}
              transition={transitionFast}
              whileHover={{ scale: prefersReducedMotion ? 1 : 1.02 }}
              whileTap={{ scale: prefersReducedMotion ? 1 : 0.98 }}
              type="button"
              aria-label={unreadCount > 0 ? `Abrir chat. ${unreadCount} mensajes nuevos` : "Abrir chat"}
              aria-haspopup="dialog"
              aria-expanded={isChatOpen}
              aria-controls="floating-chat-panel"
              className="fixed bottom-10 right-10 h-14 z-200 focus:outline-none"
              onClick={async (e) => {
                e.preventDefault()
                e.stopPropagation()
                try {
                  // If the page has reCAPTCHA enabled, require token before opening floating chat
                  const bodyAttr = document.body.getAttribute('data-recaptcha-token')
                  if (!bodyAttr) {
                    // Ask ChatHero to kick off captcha by dispatching a custom event
                    // Small delay to ensure iOS renders the reCAPTCHA anchor before our UI updates
                    setTimeout(() => {
                      window.dispatchEvent(new CustomEvent('theos:request-captcha'))
                    }, 0)
                    setShouldOpenAfterCaptcha(true)
                    return
                  }
                } catch {}
                setIsChatOpen(true)
              }}
            >
              <div className="tab-variant-light text-gray-1000/90 text-sm/5 leading-[.28px] font-normal p-4 size-full bg-gray-0/50">
                <div className="absolute top-0 left-0 w-full h-full rounded-full pointer-events-none bg-gray-1000/5">
                  <div className="absolute top-0 left-0 w-full h-full rounded-full" data-theme="morning">
                    <div className="absolute top-0 left-0 w-full h-full rounded-full tab-light-overlay-1 transition" />
                    <div className="absolute top-0 left-0 w-full h-full rounded-full tab-light-overlay-2 transition" />
                    <div className="absolute top-0 left-0 w-full h-full rounded-full tab-light-overlay-3 transition" />
                    <div className="absolute top-0 left-0 w-full h-full rounded-full tab-light-overlay-4 transition" />
                    <div className="absolute top-0 left-0 w-full h-full rounded-full tab-light-border mask-border p-px" />
                    <div className="absolute top-0 left-0 w-full h-full rounded-full tab-light-shadow" />
                  </div>
                </div>

                <div className="flex items-center gap-3 h-full">
                  <div className="relative flex size-6">
                    <Avatar className="size-full" >
                     {activeTab === "instagram" ? <AvatarImage src={"/funnel/instagram.png"} sizes="24px" /> : <AvatarImage src={"/funnel/linkedin.png"} sizes="24px" />}
                      <AvatarFallback>
                        RC
                      </AvatarFallback>
                    </Avatar>
                    {unreadCount > 0 && (
                      <div className="size-4 rounded-full bg-rose-600 shrink-0 box-border absolute -top-1 -right-1 shadow-[0_0_0_1px_#FFF] flex items-center justify-center text-xs text-gray-0 font-medium leading-none">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </div>
                    )}
                  </div>
                  <div className="text-sm font-medium">
                    Rama Castro
                  </div>

                  <div className="relative flex size-6">
                    <Avatar className="size-full" >
                      <AvatarImage src={"/agent/ramacastro-144.avif"} sizes="24px" />
                      <AvatarFallback>
                        RC
                      </AvatarFallback>
                    </Avatar>
                    <div className="size-2 rounded-full bg-green-600 shrink-0 box-border absolute -bottom-0.5 -right-0.5 shadow-[0_0_0_2px_#FFF]"></div>
                  </div>
                </div>
              </div>
            </motion.button>
          )}
        </AnimatePresence>,
        document.body
      )}



      {isMobile ? (
        <Dialog.Portal>
          <AnimatePresence>
            {isChatOpen && (
              <>
                <Dialog.Overlay asChild forceMount>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={transitionFast}
                    className={`fixed inset-0 z-[300] bg-[#dde2ee]/40 backdrop-blur-sm ${recaptchaOpen ? 'pointer-events-none' : ''}`}
                  />
                </Dialog.Overlay>
                <Dialog.Content asChild forceMount>
                  <motion.div
                    aria-describedby="floating-chat-description"
                    initial={{ y: 40, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 40, opacity: 0 }}
                    transition={transitionSlide}
                    className={`fixed bottom-0 left-0 right-0 z-[301] outline-none ${recaptchaOpen ? 'pointer-events-none' : ''}`}
                    aria-hidden={recaptchaOpen}
                  >
                    <Dialog.Title className="sr-only">Chat</Dialog.Title>
                    <Dialog.Description id="floating-chat-description" className="sr-only">
                      Chat panel to converse with Rama Castro. Contains messages and input field.
                    </Dialog.Description>
                    <div className="relative mx-3 w-auto mb-3 min-w-0" style={{ height: 'calc(var(--vvh, 100dvh) - 6rem)' }}>
                      <div className="absolute -translate-y-14 w-full flex justify-between items-center">
                        <div></div>
                        <PlatformTabs value={activeTab} onTabChange={(p) => setActiveTab(p)} />
                        <Dialog.Close asChild>
                          <button
                            type="button"
                            aria-label="Cerrar chat"
                            className="relative tab-variant-light size-10 z-20 inline-flex items-center justify-center rounded-full bg-white/70 p-1.5 text-gray-800 shadow hover:bg-white "
                          >
                            <Minus className="size-5" />
                            <div className="absolute top-0 left-0 w-full h-full rounded-full pointer-events-none bg-gray-1000/5">
                              <div className="absolute top-0 left-0 w-full h-full rounded-full" data-theme="morning">
                                <div className="absolute top-0 left-0 w-full h-full rounded-full tab-light-overlay-1 transition" />
                                <div className="absolute top-0 left-0 w-full h-full rounded-full tab-light-overlay-2 transition" />
                                <div className="absolute top-0 left-0 w-full h-full rounded-full tab-light-overlay-3 transition" />
                                <div className="absolute top-0 left-0 w-full h-full rounded-full tab-light-overlay-4 transition" />
                                <div className="absolute top-0 left-0 w-full h-full rounded-full tab-light-border mask-border p-px" />
                                <div className="absolute top-0 left-0 w-full h-full rounded-full tab-light-shadow" />
                              </div>
                            </div>
                          </button>
                        </Dialog.Close>
                      </div>
                      <div className="relative z-10 flex h-full w-full flex-col px-6 md:px-7 lg:px-8.5">
                        <div className="absolute inset-0 h-full size-full rounded-[20px] bg-[#F2F4F9] shadow-[15px_15px_39.3px_0px_rgba(178,194,221,1),10px_20px_20px_0px_rgba(121,140,182,0.2),10px_4px_15.4px_0px_rgba(133,145,172,0.2),-10px_-4px_11.3px_0px_rgba(238,241,247,0.4),-18px_-9px_32.9px_0px_rgba(255,255,255,0.5),2px_2px_1.4px_0px_rgba(255,255,255,0.56)_inset]">
                          <div className="absolute inset-0 overflow-hidden rounded-[20px] pointer-events-auto">
                            <div className="relative w-full h-full">
                              {activeTab === "instagram" ? (
                                <InstagramChat
                                  messages={instagramMessages}
                                  input={instagramInput}
                                  setInput={setInstagramInput}
                                  onSend={handleInstagramSend}
                                />
                              ) : (
                                <LinkedInChat
                                  messages={linkedinMessages}
                                  input={linkedinInput}
                                  setInput={setLinkedinInput}
                                  onSend={handleLinkedinSend}
                                />
                              )}
                            </div>
                            <div className="pointer-events-none absolute inset-0 rounded-[20px] border-18 border-[#C4DBFF] opacity-15 blur-[10px]">
                            </div>
                            <div className="pointer-events-none absolute inset-0 rounded-[20px] border-8 border-white blur-xs">
                            </div>
                            <div className="pointer-events-none absolute inset-0 rounded-[20px] border-14 border-[#4FCDFF] opacity-10 blur-[14px]">
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </Dialog.Content>
              </>
            )}
          </AnimatePresence>
        </Dialog.Portal>
      ) : (
        typeof document !== 'undefined' && createPortal(
          <AnimatePresence>
            {isChatOpen && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 20, opacity: 0 }}
                transition={transitionFast}
                className="fixed bottom-16 right-10 z-[301]"
              >
                <div className="relative h-[560px] w-[420px]">
                  <div className="absolute -translate-y-14 w-full flex justify-between items-center">
                    <div></div>
                    <PlatformTabs value={activeTab} onTabChange={(p) => setActiveTab(p)} />
                    <button
                      type="button"
                      aria-label="Cerrar chat"
                      onClick={() => setIsChatOpen(false)}
                      className="relative tab-variant-light size-10 z-20 inline-flex items-center justify-center rounded-full bg-white/70 p-1.5 text-gray-800 shadow hover:bg-white "
                    >
                      <Minus className="size-5" />
                      <div className="absolute top-0 left-0 w-full h-full rounded-full pointer-events-none bg-gray-1000/5">
                        <div className="absolute top-0 left-0 w-full h-full rounded-full" data-theme="morning">
                          <div className="absolute top-0 left-0 w-full h-full rounded-full tab-light-overlay-1 transition" />
                          <div className="absolute top-0 left-0 w-full h-full rounded-full tab-light-overlay-2 transition" />
                          <div className="absolute top-0 left-0 w-full h-full rounded-full tab-light-overlay-3 transition" />
                          <div className="absolute top-0 left-0 w-full h-full rounded-full tab-light-overlay-4 transition" />
                          <div className="absolute top-0 left-0 w-full h-full rounded-full tab-light-border mask-border p-px" />
                          <div className="absolute top-0 left-0 w-full h-full rounded-full tab-light-shadow" />
                        </div>
                      </div>
                    </button>
                  </div>
                  <div className="relative z-10 flex h-full w-full flex-col px-6 md:px-7 lg:px-8.5">
                    <div className="absolute inset-0 h-full size-full rounded-[20px] bg-[#F2F4F9] shadow-[15px_15px_39.3px_0px_rgba(178,194,221,1),10px_20px_20px_0px_rgba(121,140,182,0.2),10px_4px_15.4px_0px_rgba(133,145,172,0.2),-10px_-4px_11.3px_0px_rgba(238,241,247,0.4),-18px_-9px_32.9px_0px_rgba(255,255,255,0.5),2px_2px_1.4px_0px_rgba(255,255,255,0.56)_inset]">
                      <div className="absolute inset-0 overflow-hidden rounded-[20px] pointer-events-auto">
                        <div className="relative w-full h-full">
                          {activeTab === "instagram" ? (
                            <InstagramChat
                              messages={instagramMessages}
                              input={instagramInput}
                              setInput={setInstagramInput}
                              onSend={handleInstagramSend}
                            />
                          ) : (
                            <LinkedInChat
                              messages={linkedinMessages}
                              input={linkedinInput}
                              setInput={setLinkedinInput}
                              onSend={handleLinkedinSend}
                            />
                          )}
                        </div>
                        <div className="pointer-events-none absolute inset-0 rounded-[20px] border-18 border-[#C4DBFF] opacity-15 blur-[10px]">
                        </div>
                        <div className="pointer-events-none absolute inset-0 rounded-[20px] border-8 border-white blur-xs">
                        </div>
                        <div className="pointer-events-none absolute inset-0 rounded-[20px] border-14 border-[#4FCDFF] opacity-10 blur-[14px]">
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )
      )}
    </Dialog.Root>
  )
}

interface ChatProps {
  messages: Message[]
  input: string
  setInput: (value: string) => void
  onSend: () => void
}

